import { EntityType, FeatureType, GetCommentResponse, IntegrationException, SearchFilter, TiqetsResponseProduct, TopicSearchResult, TranslationPhrases } from '../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { getJLLFormHMTL } from '../helpers/constants';
import { renderEmbedlyCards } from '../helpers/embeds';
import { TemplateResult, css, html, unsafeCSS } from 'lit';
import { addPointView, getTopics } from '../helpers/api/topics-api';
import { customElement, property, state } from 'lit/decorators';
import { detectMobileViewport, getCookie } from '../helpers/device';
import { editPointUpvote, pointRequestFromPoint } from '../helpers/api/points-api';
import { getCategoryIcon, getNumberedPointIconColor, getNumberedPointIconShape } from '../helpers/markers';
import { getVisibleTextColor, isGeneratedColorIcon, makeColorsIntoNumberSvg } from '../helpers/colors';
import { isDefined, isFalsy, isValidString, shouldDisplayAddPointElement, undefinedIfEmpty } from '../helpers/utils';

import { PointCardBase } from './point-card-base';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { addEmailCapture } from '../helpers/api/email-capture-api';
import challengeData from '../helpers/state/challenge-data';
import featuresInfo from '../helpers/state/features-info';
import { getMainPage } from '../helpers/page-name';
import { getPointComments } from '../helpers/api/comments-api';
import { heights } from '../style/variables';
import { ifDefined } from 'lit/directives/if-defined';
import pointCache from '../helpers/state/point-cache';
import { shouldRevealSpecialOffer } from '../helpers/challenge-special-offers';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { validateEmail } from '../helpers/strings';

@customElement('expanded-point-card')
export class ExpandedPointCard extends PointCardBase {
    static amplitude_callsite: string = 'Point Card';

    @property({ type: Array })
    associatedMaps: TopicSearchResult[] = [];

    @property({ type: Boolean })
    discoveryView: boolean = false; // when cards are on discovery page, we use a transparent background, only show one photo

    @property({ type: Boolean })
    listOfPointsView: boolean = false;

    @property({ type: Boolean })
    hideImages: boolean = false;

    @property({ type: Boolean })
    isBackpackPage: boolean = false;

    @property({ type: Array })
    bookingProducts: TiqetsResponseProduct[] = [];

    @property({ type: String })
    customHeight?: string;

    @property({ type: String })
    customImageHeight?: string;

    @property({ type: Boolean })
    hideTopMenu: boolean = false;

    @state()
    isCheckedin = false;

    @state()
    allComments: GetCommentResponse[] = [];

    @state()
    showSpecialOffer: boolean = true;

    @state()
    showForm: boolean = false;

    @state()
    showComments: boolean = false;

    @state()
    showBooking: boolean = false;

    @state()
    ip_address: string = '';

    @state()
    previewMode: boolean = false;

    @state()
    mobilePreview: boolean = false;

    @state()
    listMode: boolean = false;

    @state()
    customFormInputs: Array<{ [key: string]: string | TemplateResult }> = [];

    @state()
    eventString: string = '';

    @state()
    translatedStrings: TranslationPhrases = {};

    get showInfo() {
        return !detectMobileViewport(this.mobilePreview);
    }

    private hasCustomCategoryIcon() {
        const icon = this.category?.icon ?? '';

        if (icon.startsWith('#')) {
            return false;
        }

        if (isGeneratedColorIcon(icon)) {
            return false;
        }

        return icon.length > 0;
    }

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    translationStrings: TranslationPhrases = {
        nameLabel: topicData.topic?.point_email_capture_customization?.name_field?.label ?? 'Name',
        emailLabel: topicData.topic?.point_email_capture_customization?.email_field?.label ?? 'Email',
        namePlaceholder: topicData.topic?.point_email_capture_customization?.name_field?.placeholder ?? 'Name',
        emailPlaceholder: topicData.topic?.point_email_capture_customization?.email_field?.placeholder ?? 'Email',
        phonePlaceholder: topicData.topic?.point_email_capture_customization?.phone_field?.placeholder ?? 'Phone',
        phoneLabel: topicData.topic?.point_email_capture_customization?.phone_field?.label ?? 'Phone',
        additionalInfoPlaceholder: topicData.topic?.point_email_capture_customization?.additional_info_field?.placeholder ?? 'Additional Info',
        additionalInfoLabel: topicData.topic?.point_email_capture_customization?.additional_info_field?.label ?? 'Additional Info'
    };

    async connectedCallback() {
        super.connectedCallback();

        // Add a listener for the 'languageChanged' event
        translationData.onLanguageChanged(() => {
            void this.translateFields();
        });
    }

    async firstUpdated() {
        this.updateCheckinStatus();
        await this.translateFields();

        // Track point view/display (client-side Google Analytics + server-side telemetry)
        if (this.point) {
            try {
                await addPointView(ExpandedPointCard.amplitude_callsite, topicData.topicId, this.point._id.$oid, 'expanded_card');
            } catch (e) {
                console.error('Error tracking point view', e);
            }
        }
    }

    async translateFields() {
        this.translatedStrings = await translationData.translateFields(this.translationStrings);
        this.requestUpdate();
    }

    isURLType(field: any) {
        return isDefined(field?.URL);
    }

    private async commentChanged() {
        this.allComments = await getPointComments(this.point?._id.$oid ?? '', topicData.topicKey);
    }

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        await super.updated(changedProperties);

        if (changedProperties.has('point')) {
            // If the point is minimal, fetch the full point data before displaying
            if (this.point?.is_minimal === true) {
                try {
                    await pointCache.refreshPoint(this.point._id.$oid);
                    this.point = pointCache.getPoint(this.point._id.$oid);
                } catch (e) {
                    console.error('Error fetching full point data', e);
                }
            }

            // set Custom Form Inputs from
            this.customFormInputs = [];
            this.customFormInputs = this.customFieldCardDisplay;
            try {
                this.allComments = await getPointComments(this.point?._id.$oid ?? '', topicData.topicKey);
                this.associatedMaps = (await this.getAssociatedMaps()) ?? [];
            } catch (e) {
                console.error('Error getting comments or maps', e);
            }

            if (!this.customHeight) {
                if (topicData.topic?.has_dates === true) {
                    this.eventString = await this.getEventString() ?? '';
                };
            } else {
                // set the outer height of the host element
                this.style.height = this.customHeight;
                this.style.overflow = 'hidden';
                // get expanded-point-card--horizontal and set its height to 100%
                const expandedPointCardHorizontal = this.shadowRoot?.querySelector('.expanded-point-card--horizontal') as HTMLElement;
                if (isDefined(expandedPointCardHorizontal)) {
                    expandedPointCardHorizontal.style.height = '100%';
                }
                const expandedPointCardVertical = this.shadowRoot?.querySelector('.expanded-point-card--vertical') as HTMLElement;
                if (isDefined(expandedPointCardVertical)) {
                    expandedPointCardVertical.style.height = '100%';
                }
                this.requestUpdate();

            }

            if (this.customImageHeight) {
                this.style.setProperty('--point-card-image-height', this.customImageHeight);
            }

            this.requestUpdate();
        }

        if (changedProperties.has('point') && isDefined(this.point)) {
            renderEmbedlyCards(this.shadowRoot?.querySelectorAll('a[href*="proxi_embed=true"]') ?? []);
            // try {
            //     await getBookingInformation(this.point).then((result) => {
            //         // filter out products that are not available, have no reivews, or are less than 4 stars
            //         result.products = result.products.filter(
            //             (product) =>
            //                 product.sale_status === 'available' &&
            //                 product.ratings.total > 0 &&
            //                 product.ratings.average >= 4
            //         );
            //         this.bookingProducts = result.products;
            //     });
            // } catch (e) {
            //     // do nothing
            // }
        }
    }

    private onToggleForm() {
        this.showForm = !this.showForm;
        this.showComments = false;
        this.imageUrls = [];

        // Track form visibility
        if (this.showForm && this.point) {
            logWithGoogleTagAndTopicOrganization(ExpandedPointCard.amplitude_callsite, GoogleTagEvent.POINT_FORM_VIEWED, {
                form_submitted: false,
                card_type: 'expanded'
            });
        }
    }

    private onToggleComments() {
        this.showComments = !this.showComments;
        if (this.showComments) {
            this.showForm = false;
            this.imageUrls = [];

        }
    }

    private async emailCaptureSubmit() {
        const email = (this.shadowRoot?.querySelector('#pointFormEmail') as HTMLInputElement)?.value;
        const name = (this.shadowRoot?.querySelector('#pointFormName') as HTMLInputElement)?.value;
        if (email) {
            try {
                const lowerEmail = email?.trim().toLowerCase() ?? '';
                const emailValid = validateEmail(lowerEmail);
                const buttonText = topicData.topic?.point_email_capture_customization?.cta_button_text ?? 'Submit';

                // create additional info string the concatenates phone number and additional info if it exists.
                const phone = (this.shadowRoot?.querySelector('#pointFormPhone') as HTMLInputElement)?.value;
                const additionalInfo = (this.shadowRoot?.querySelector('#pointFormAdditionalInfo') as HTMLInputElement)?.value;
                let additionalInfoString = '';

                if (phone) {
                    additionalInfoString += `Phone: ${phone}\n`;
                }

                if (additionalInfo) {
                    additionalInfoString += 'Additional Info: ' + additionalInfo;
                }

                if (emailValid) {
                    const emailCaptureRequest = {
                        email: lowerEmail,
                        ip_address: '',
                        text: {
                            title: this.point?.name ?? '',
                            description: this.point?.description ?? '',
                            cta: buttonText ?? ''
                        },
                        name,
                        point_id: this.point?._id.$oid,
                        additional_info: additionalInfoString,
                    };

                    await addEmailCapture(ExpandedPointCard.amplitude_callsite, topicData.topicId, emailCaptureRequest);

                    // Track form submission
                    logWithGoogleTagAndTopicOrganization(ExpandedPointCard.amplitude_callsite, GoogleTagEvent.POINT_FORM_VIEWED, {
                        form_submitted: true,
                        card_type: 'expanded',
                        lead_email: lowerEmail
                    });

                    getMainPage().errorToastElement.showToast(topicData.topic?.point_email_capture_customization?.success_message ?? 'Response successfully captured.');

                    this.showForm = false;
                } else {
                    getMainPage().errorToastElement.showToastAndThrow('Invalid email format.');
                }
            }
            catch (e) {
                getMainPage().errorToastElement.showToastAndThrow('An error has occurred.  Context: could not add email capture.');
            }
        } else {
            getMainPage().errorToastElement.showToastAndThrow('Please enter an email address.');
        }
    }

    private updateCheckinStatus() {
        if (isDefined(this.point)) {
            this.isCheckedin = pointCache.isUserCheckedIn(this.point);
            this.showSpecialOffer = true;

            if (topicData.hasChallenge) {
                const challenge = challengeData.challenge;
                if (!challenge) {
                    return;
                }
                this.showSpecialOffer = shouldRevealSpecialOffer({
                    challenge,
                    isCheckedIn: this.isCheckedin,
                    isGuide: false,
                });
            }
        }
    }

    private async getAssociatedMaps() {
        if (this.point === undefined || topicData.topic?.hide_featured_on === true) {
            return;
        }
        const searchFilter: SearchFilter = {
            location_match: {
                location: this.point.location,
                max_distance: 5,
            },
        };
        if (topicData.topic?.organization_id) {
            searchFilter.organization_ids = [topicData.topic?.organization_id];
        }
        const topicsWithPoint =
            (await getTopics(PointCardBase.amplitude_callsite, searchFilter)) ?? [];
        let allAssociatedMaps = topicsWithPoint.filter((map) => map._id.$oid !== topicData.topicId);
        allAssociatedMaps = allAssociatedMaps.slice(0, 3);
        // [mhaughey] Feb 6 removed a highly inefficient call to get all points and then filter them down to just the points associated with the current point.  This was causing performance issues when there were a large number of points.  Instead we will now just check if the current point is in the associated maps and if so we will add it to the list of associated maps.
        // It really isn't that important to link to the exact point on the map at the moment
        return allAssociatedMaps;
    }

    private productUrl(url: string) {
        return `${url}&tq_campaign=${topicData.topic?._id.$oid ?? ''}`;
    }

    private async editClick() {
        if (this.point) {
            await topicData.mapComponent?.onPointEdit(this.point);
        }

        logWithAmplitude(ExpandedPointCard.amplitude_callsite, 'Edit Click', {
            point_id: this.point_id,
            topic_id: topicData.topicId,
        });
    }

    private async onShareUrl() {
        if (this.point) {
            await topicData.mapComponent?.onPointShare(this.point);

            logWithAmplitude(ExpandedPointCard.amplitude_callsite, 'Shared Click', {
                topic_id: topicData.topicId,
                point_id: this.point_id,
                point_name: this.point?.name,
            });
        }
    }

    private trackBookingClick(experienceName: string) {
        logWithAmplitude(ExpandedPointCard.amplitude_callsite, 'Booking Click', {
            point_id: this.point_id,
            topic_id: topicData.topicId,
            experience_name: experienceName,
        });
    }

    private parseDomain(url: string) {
        let domain = url;

        // Remove protocol (e.g., "http://", "https://")
        if (domain.includes('://')) {
            domain = domain.split('://')[1];
        }

        // Remove path (e.g., "/path/to/page")
        if (domain.includes('/')) {
            domain = domain.split('/')[0];
        }

        // Remove www subdomain
        if (domain.indexOf('www.') === 0) {
            domain = domain.substring(4);
        }

        return domain;
    }

    private distance(lat1: number, lon1: number, lat2: number, lon2: number): number {
        if (lat1 === lat2 && lon1 === lon2) {
            return 0;
        } else {
            const radlat1 = (Math.PI * lat1) / 180;
            const radlat2 = (Math.PI * lat2) / 180;
            const theta = lon1 - lon2;
            const radtheta = (Math.PI * theta) / 180;
            let dist =
                Math.sin(radlat1) * Math.sin(radlat2) +
                Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = (dist * 180) / Math.PI;
            dist = dist * 60 * 1.1515;
            dist = dist * 1.609344 * 1000; // convert to meters
            return dist;
        }
    }

    private async toggleUpvote() {
        if (this.hasThisUserUpvoted) {
            await this.editUpvotePoint();

            logWithAmplitude(PointCardBase.amplitude_callsite, 'Remove Upvote Click', {
                point_id: this.point_id,
                topic_id: topicData.topicId,
            });

        } else {
            await this.editUpvotePoint();

            logWithAmplitude(PointCardBase.amplitude_callsite, 'Upvote Click', {
                point_id: this.point_id,
                topic_id: topicData.topicId,
            });

            logWithGoogleTagAndTopicOrganization(PointCardBase.amplitude_callsite, GoogleTagEvent.POINT_LIKED, {
                point_id: this.point_id,
                point_name: this.point?.name,
            });
        }
        this.requestUpdate();
    }

    private async editUpvotePoint() {
        const userString = getCookie('user_id');

        if (isDefined(this.point) && userString) {
            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(this.point)) {
                    await editPointUpvote(
                        ExpandedPointCard.amplitude_callsite,
                        topicData.topicId,
                        this.point._id.$oid,
                        topicData.topicKey
                    );

                    await pointCache.refreshPoint(this.point._id.$oid);
                    this.point = pointCache.getPoint(this.point._id.$oid);
                }
            }, `An error has occurred.  Context: could not upvote the point.`);
        }
    }

    private async onActivateClick() {
        if (featuresInfo.pointsPerMapEnabled < pointCache.allPoints.length) {
            await topicData.mapComponent?.openUpgradeDialog(FeatureType.PointsPerMap);
            return;
        }

        if (isDefined(this.point)) {
            this.point.active = true;
            const pointRequest = pointRequestFromPoint(this.point);

            await getMainPage().errorToastElement.tryOrErrorToast(async () => {
                if (isDefined(this.point)) {
                    await pointCache.modifyAndFetch(this.point._id.$oid, pointRequest, true);
                }

            }, 'An error has occurred.  Context: could not activate the point.');
        }

        logWithAmplitude(ExpandedPointCard.amplitude_callsite, 'Activate Click', {
            point_id: this.point?._id.$oid,
            topic_id: topicData.topicId
        });
    }

    async closePointCard() {
        if (!detectMobileViewport() || topicData.mapComponent?.pointCardPaginationEl.pointsToRender.size === 1) {
            await this.onExitHoverMarker();
            topicData.mapComponent?.closeListOfPoints();
        } else {
            topicData.mapComponent?.removeAllFocus();
            await this.onExitHoverMarker();
            // send event to close expanded card on mobile
            this.dispatchEvent(new CustomEvent('close-expanded-point-card', { bubbles: true, composed: true }));
        }
    }

    render() {
        return html`
            <div
                class=" ${this.discoveryView || this.listOfPointsView
                    ? 'expanded-point-card--discovery'
                    : 'expanded-point-card'}
                        ${detectMobileViewport(this.mobilePreview)
                    ? 'expanded-point-card--vertical'
                    : 'expanded-point-card--horizontal'}
                    ${this.isBackpackPage ? 'expanded-point-card--backpack' : ''}
                        ${this.canShowAdminControls && !this.previewMode
                    ? 'expanded-point-card--horizontal_admin'
                    : 'expanded-point-card--horizontal_cc'}
                        ${this.listOfPointsView ? 'no-bottom-border small-text' : ''}"
                @mouseenter=${this.onHoverMarker} @mouseleave=${this.onExitHoverMarker}
            >
                ${!this.discoveryView && !this.listOfPointsView
                    ? html`
                    ${this.isBackpackPage ? '' : html`
                        <div
                            class="${this.customImageHeight ? 'expanded-point-card__image-wrapper--custom' : 'expanded-point-card__image-wrapper'} ${!this.hasImages || this.hideImages
                                ? 'expanded-point-card__image-wrapper--no-image'
                                : ''}"
                        >
                          ${this.hasImages
                              ? html`<image-carousel
                                        @image-error="${(e: CustomEvent) => this.handleImageError(e.detail)}"
                                        .height=${this.customImageHeight ?? '300px'}
                                        borderRadius="4" .imageUrls=${this.displayableImageUrls} .mobilePreview=${this.mobilePreview}>
                                </image-carousel>`
                              : ''}

                            <span class="expanded-point-card__category-wrapper ${topicData.hasExceptions === IntegrationException.AustraliaMeatAndLivestock || topicData.hasExceptions === IntegrationException.TeamBuildingAnywhere ? 'hidden' : ''}">
                                ${this.isCheckedin
                                    ? html`<img
                                            src="/images/checked_in.png"
                                            class="expanded-point-card__checked-in"
                                            width="26"
                                            height="26"
                                        />`
                                    : ''}
                                ${topicData.topic?.enable_point_numbering === true &&
                                this.point?.number !== undefined
                                    ? html`<img
                                            src="${makeColorsIntoNumberSvg(
                                                getNumberedPointIconColor(topicData.topic, this.point.topic_category_id.$oid),
                                                this.point?.number,
                                                getNumberedPointIconShape(topicData.topic, this.point.topic_category_id.$oid)
                                            )}"
                                            class="expanded-point-card__point-number"
                                            width="26"
                                            height="26"
                                        />`
                                    : ''}
                                ${undefinedIfEmpty(this.point?.custom_icon_url) !== undefined
                                    ? html`<img
                                          src="${ifDefined(this.point?.custom_icon_url)}"
                                          class="expanded-point-card__custom-icon"
                                          width="26"
                                          height="26"
                                      />`
                                    : ''}
                                ${this.associatedMarker?.badges?.map((badge) => {
                                    return html`<img
                                        src="${badge.icon}"
                                        class="expanded-point-card__badge"
                                        width="26"
                                        height="26"
                                    />`;
                                }  )}
                                ${(topicData.topic?.enable_point_numbering === true && this.point?.number !== undefined) && !this.hasCustomCategoryIcon()
                                    ? ''
                                    : html`<img src="${getCategoryIcon(this.category?._id)}" width="26" height="26" />`
                                }
                                <translated-span class="expanded-point-card__category-name">${this.category?.name}</translated-span>
                            </span>
                          <div class="expanded-point-card__menu-wrapper" style="${this.hideTopMenu ? 'display:none' : ''}">
                              ${this.canShowAdminControls && !this.previewMode
                                  ? html`<mwc-icon-button
                                            raised
                                            class="expanded-point-card__edit"
                                            @click="${this.editClick}"
                                            icon="edit"
                                        ></mwc-icon-button>
                                        ${this.point?.active === false ?
                                            html`<button
                                                class="expanded-point-card__edit"
                                                @click="${this.onActivateClick}"
                                                style="padding:8px"
                                            >Approve</button>`
                                            : ''
                                        }
                                        ${this.showInfo
                                            ? html`<form-tooltip
                                                  tipwidth="100px"
                                                  position="bottom"
                                                  message=${this.creationInfo}
                                              >
                                                  <mwc-icon-button
                                                      raised
                                                      class="expanded-point-card__info"
                                                      icon="${this.numUpvotes && this.numUpvotes > 0 ? 'thumb_up' : 'info'}"
                                                  > ${this.numUpvotes && this.numUpvotes > 0 ?
                                                        html`
                                                            <span style="font-size: 0.8rem;">${this.numUpvotes}</span>`
                                                        : ''}
                                                </mwc-icon-button>
                                              </form-tooltip>`
                                            : ''}
                                        <point-card-menu
                                            .point=${this.point}
                                        >
                                        </point-card-menu>`
                                  : html`${topicData.topic?.has_likes === true
                                        ? html`<form-tooltip
                                              tipwidth="60px"
                                              position="bottom"
                                              message=${isDefined(this.numUpvotes)
                                                  ? `${this.numUpvotes} upvotes`
                                                  : 'Be the first to upvote!'}
                                          >
                                              <form-button
                                                  raised
                                                  .borderStyle=${'round'}
                                                  .backgroundColor=${'white'}
                                                  size="sm"
                                                  @click="${this.toggleUpvote}"
                                                  class="expanded-point-card__upvote"
                                              >
                                                  ${this.hasThisUserUpvoted
                                                      ? html`<img
                                                            src="/images/heart_filled.png"
                                                            class="expanded-point-card__upvote-icon"
                                                            alt="upvote"
                                                        />`
                                                      : html`<img
                                                            src="/images/heart_unfilled.png"
                                                            class="expanded-point-card__upvote-icon"
                                                            alt="remove upvote"
                                                        />`}
                                                        ${this.numUpvotes && this.numUpvotes > 0 ? html`
                                                            <span style="font-size: 0.8rem;color:var(--main-primary-color);margin-left:4px">${this.numUpvotes}</span>`
                                                            : ''}
                                              </form-button>
                                          </form-tooltip>`
                                        : ''}
                                        ${topicData.topic?.disable_share_button === true ? '' : html`
                                            <mwc-icon-button
                                                raised
                                                @click="${this.onShareUrl}"
                                                class="expanded-point-card__share"
                                                icon="ios_share"
                                            ></mwc-icon-button>
                                        `}
                                    `}
                              ${this.canShowAdminControls
                                  ? html`<mwc-icon-button
                                        raised
                                        @click="${this.closePointCard}"
                                        class="expanded-point-card__close_admin"
                                        icon="close"
                                    >
                                    </mwc-icon-button>`
                                  : html`<mwc-icon-button
                                        raised
                                        @click="${this.closePointCard}"
                                        class="expanded-point-card__close_cc"
                                        icon="close"
                                    >
                                    </mwc-icon-button>`}
                            </div>
                            </div>`

                        }
                      </div>`
                    : ''}
                <div
                    class="${!this.discoveryView && !this.listOfPointsView
                        ? 'expanded-point-card__content'
                        : 'expanded-point-card__content--discovery'}
                            ${detectMobileViewport(this.mobilePreview)
                        ? 'expanded-point-card__content--horizontal'
                        : 'expanded-point-card__content--vertical'}
                        ${this.listOfPointsView ? 'expanded-point-card__content--list-of-points' : ''}"
                >
                    <span
                        class="${this.discoveryView || this.listOfPointsView
                            ? 'expanded-point-card__discovery-content'
                            : ''}"
                    >
                        ${this.canShowAdminControls && !this.previewMode && this.listOfPointsView ? html`
                            <p class="admin-action-list-holder" style="line-height: 2rem;;">
                                <span class="admin-actions-list" @click="${this.editClick}">
                                    Edit Point
                                </span>
                            </p>
                        ` : ''}
                        ${(this.discoveryView || this.listOfPointsView) && this.hasImages && (this.imageUrls?.length ?? 0) > 0
                            ? html`<img
                                  class="expanded-point-card__single-image"
                                  src=${ifDefined(this.imageUrls?.[0].image)}
                              />`
                            : ''}
                        ${shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.title?.hide_on_point_card, this.previewMode) && !this.listOfPointsView ? html`
                            <span class="expanded-point-card__title">
                                ${this.point?.featured === true
                                    ? html` <mwc-icon
                                        class="expanded-expanded-point-card__title--featured"
                                        >star</mwc-icon
                                    >`
                                    : ''}
                                ${isValidString(this.point?.name)
                                    ? html`<h2 class="expanded-expanded-point-card__title-text">
                                        ${this.point?.name}
                                    </h2>`
                                    : ''}
                            </span>`
                        : ''}
                        ${this.point?.location.place_id !== 'custom' &&
                        isFalsy(topicData.topic?.disable_location_ui) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.location?.hide_on_point_card, this.previewMode)
                            ? html`<div class="expanded-point-card__address">
                                  <mwc-icon class="expanded-point-card__address-icon">
                                      location_on
                                  </mwc-icon>
                                  ${this.point?.location.search === 'Shape' ? html`<translated-span>Custom Area</translated-span>` : html`<translated-span>${this.point?.location.search}</translated-span>`}
                              </div>`
                            : ''}
                        ${isValidString(this.eventString) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.date_time?.hide_on_point_card)
                            ? html` <div class="expanded-point-card__event">
                                  <mwc-icon class="expanded-point-card__event-icon">
                                      calendar_month
                                  </mwc-icon>
                                  ${topicData.topic?.add_point_customization?.date_time?.header
                                      ? html`<span class="expanded-point-card__event-label">${topicData.topic.add_point_customization.date_time.header}:</span> `
                                      : ''}${this.eventString}
                              </div>
                              ${this.point?.is_out_of_date ?? false ? html`
                                <div class="expanded-point-card__event" style="color:var(--main-invalid-color);">
                                    <span>
                                        <translated-span>This point is out of date and hidden to the public. You are seeing it because you are an admin.</translated-span>
                                    </span>
                                </div>`
                            : ''}
                              `
                            : ''}
                        ${topicData.topic?.comment_settings?.allow_comments === true ? html`
                            ${this.showComments ? '' : html`
                                <div class="expanded-point-card__event" style="color:var(--main-primary-opacity-color);" @click="${this.onToggleComments}">
                                    <mwc-icon class="expanded-point-card__event-icon">comment</mwc-icon>
                                    <span style=" text-decoration:underline;cursor:pointer;">${this.allComments.length > 0 ?
                                        html`${this.allComments.length} <translated-span>${this.allComments.length === 1 ? (topicData.topic?.comment_settings?.comment_title?.singular ?? 'Comment') : (topicData.topic?.comment_settings?.comment_title?.plural ?? 'Comment')}</translated-span>` :
                                        html`Add ${topicData.topic?.comment_settings?.comment_title?.singular ?? 'Comment'}`}</span>
                                        </span>
                                </div>`
                            }
                        ` : ''}
                        ${this.showSpecialOffer && isValidString(this.point?.special_offer?.description) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.special_offer?.hide_on_point_card, this.previewMode)
                            ? html` <span class="expanded-point-card__special-offer">
                                  <b>${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}:</b>
                                  ${this.point?.special_offer?.description}
                              </span>`
                            : ''}
                        ${this.showForm || this.showComments ? '' : html`
                            ${isValidString(this.descriptionString) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.description?.hide_on_point_card, this.previewMode)
                                ? html` <span class="expanded-point-card__description">
                                    ${html`${unsafeHTML(this.descriptionString)}`}
                                </span>`
                                : ''}
                            ${this.customFormInputs.map((input) => {
                                return html`
                                    ${Object.keys(input).map((key) => {
                                        return html`
                                            <span class="expanded-point-card__custom-form">
                                                <br>
                                                <b><translated-span>${key}</translated-span>:</b> ${input[key]}
                                            </span>
                                        `;
                                    })}
                                `;
                            }
                            )}`
                        }

                        ${this.associatedMaps.length > 0 && !this.showForm && !this.showComments
                            ? html` <div class="expanded-point-card__associated-wrapper">
                                  <translated-span>Also featured on:</translated-span><br>
                                  <div class="expanded-point-card__associated-maps">
                                    ${this.associatedMaps.map((topic) => {
                                        return html`
                                            <backpack-card
                                                style="width: 40%;margin-top: 5px;"
                                                class="topic"
                                                .topic=${topic}
                                                .mini=${true}
                                                ?goToDetailPage=${false}
                                                ?hideViewerType=${true}
                                                ?hideMetadata=${true}
                                                ?hideBookmarkButton=${true}
                                            ></backpack-card>
                                        `;
                                    })}
                                    </div>
                              </div>`
                            : ''}
                        ${isDefined(this.point) &&
                        isDefined(this.point.source) &&
                        isValidString(this.point.source.source_url) && topicData.hasExceptions !== IntegrationException.JLLRealEstate // tk the most shameless hack for JLL Customer - do data clean for them someday
                            ? html` <div class="expanded-point-card__source-wrapper">
                                  Pinned from:
                                  <a
                                      class="expanded-point-card__source"
                                      target="${topicData.topic?.open_links_in_same_tab === true ? '_self' : '_blank'}"
                                      href=${this.point.source.source_url}
                                      >${this.point?.source.source_name ??
                                      this.parseDomain(this.point.source.source_url)}</a
                                  >
                              </div>`
                            : ''}
                        ${(this.pointTags.length > 0 || this.discoveryView) && shouldDisplayAddPointElement(topicData.topic?.add_point_customization?.tags?.hide_on_point_card, this.previewMode)
                            ? html` <div class="point-card__tags-wrapper">
                                  ${this.discoveryView
                                      ? html`<div class="expanded-point-card__tag">
                                            <img
                                                src="${this.category?.icon ?? ''}"
                                                width="16"
                                                height="16"
                                            />
                                            <translated-span>${this.category?.name}</translated-span>
                                        </div>`
                                      : ''}
                                  ${this.pointTags?.map(
                                      (item) =>
                                          html`
                                              <div
                                                  class="expanded-point-card__tag"
                                                  style="background-color: ${item.icon}; color: ${getVisibleTextColor(
                                                      item.icon
                                                  )}"
                                              >
                                                ${item.name === 'Intentionalist' ? html`
                                                     <img src="/images/intentionalist.jpg" width="16" height="16" style="border-radius: 50%;margin-right: 5px;">
                                                ` : html`
                                                    <mwc-icon class="expanded-point-card__tag-icon"
                                                        >sell</mwc-icon
                                                    >
                                                `}
                                                  <translated-span>${item.name}</translated-span>
                                              </div>
                                          `
                                  )}
                              </div>`
                            : ''}
                        ${topicData.hasExceptions === IntegrationException.FranchiseDemographics && isDefined(this.point?.location?.latLng)
                            ? html`<demographics-panel
                                  .lat=${this.point?.location.latLng.lat}
                                  .lng=${this.point?.location.latLng.lng}
                              ></demographics-panel>`
                            : ''}
                    </span>
                    ${this.showForm ? html`
                        <div class="expanded-point-card__form">
                            ${topicData.hasExceptions === IntegrationException.JLLRealEstate ? this.renderJllCustomForm() : this.renderPointCartForm()}
                        </div>
                    ` : ''}

                    ${this.showComments ? html`
                        <div class="expanded-point-card__form">
                            <comment-component
                                @close=${this.onToggleComments}
                                @comment-changed=${async () => await this.commentChanged()}
                                .allComments=${this.allComments}
                                .entity_id=${this.point?._id.$oid}
                                .entity_type=${EntityType.Point}>
                            </comment-component>
                        </div>
                    ` : ''}

                </div>
                ${this.listOfPointsView && isDefined(topicData.mapComponent) && isDefined(this.associatedMarker) ? html`
                    <div style="padding: 4px 8px;">
                        <form-button
                            variant="outline"
                            size="sm"
                            fullWidth
                            @click=${async () => {
                                if (this.associatedMarker && this.point) {
                                    await topicData.mapComponent?.closeMapDrawer();
                                    await topicData.mapComponent?.focusMarker(this.associatedMarker, this.point);
                                }
                            }}
                        ><mwc-icon aria-hidden="true" style="--mdc-icon-size:16px; margin-right:4px">place</mwc-icon> View on Map</form-button>
                    </div>
                ` : ''}
                <point-card-actions
                    style="padding: 0 8px;"
                    .point=${this.point}
                    .hideCreateMap=${this.discoveryView || this.listOfPointsView}
                    .mobilePreview=${this.mobilePreview}
                    @on-toggle-form=${this.onToggleForm}
                ></point-card-actions>
            </div>

            <mwc-dialog class="dialog" hideActions>
                <modal-dialog size="lg">Add To Map</modal-dialog>
            </mwc-dialog>


            <error-toast id="errorToast"></error-toast>
        `;
    }

    renderPointCartForm() {
        return html`
        ${topicData.topic?.point_email_capture_customization?.name_field?.is_active === true ? html`
                            <proxi-textfield
                                id="pointFormName"
                                class="emailInputForm"
                                maxlength="100"
                                placeholder=${this.translatedStrings.namePlaceholder}
                                ?required=${((Boolean((topicData.topic?.point_email_capture_customization?.name_field?.is_required))) && topicData.topic?.point_email_capture_customization?.name_field?.is_active) ?? true}
                                type="text"
                                outlined
                                label=${this.translatedStrings.nameLabel}
                            ></proxi-textfield>
                            ` : ''}
                            <proxi-textfield
                                id="pointFormEmail"
                                class="nameInputForm"
                                maxlength="100"
                                required
                                type="email"
                                placeholder=${this.translatedStrings.emailPlaceholder}
                                outlined
                                label=${this.translatedStrings.emailLabel}
                            ></proxi-textfield>
                            ${topicData.topic?.point_email_capture_customization?.phone_field.is_active === true ? html`
                            <proxi-textfield
                                id="pointFormPhone"
                                class="emailInputForm"
                                maxlength="100"
                                placeholder=${this.translatedStrings.phonePlaceholder}
                                ?required=${((Boolean((topicData.topic?.point_email_capture_customization?.phone_field?.is_required))) && topicData.topic?.point_email_capture_customization?.phone_field?.is_active) ?? false}
                                type="text"
                                outlined
                                label=${this.translatedStrings.phoneLabel}
                            ></proxi-textfield>
                            ` : ''}
                            ${topicData.topic?.point_email_capture_customization?.additional_info_field.is_active === true ? html`
                            <proxi-textarea
                                id="pointFormAdditionalInfo"
                                class="emailInputForm"
                                maxlength="100"
                                placeholder=${this.translatedStrings.additionalInfoPlaceholder}
                                ?required=${((Boolean((topicData.topic?.point_email_capture_customization?.additional_info_field?.is_required))) && topicData.topic?.point_email_capture_customization?.additional_info_field?.is_active) ?? false}
                                type="text"
                                outlined
                                label=${this.translatedStrings.additionalInfoLabel}
                            ></proxi-textarea>
                            ` : ''}
                            <form-button
                                id="pointFormSubmit"
                                ?fullWidth=${true}
                                size="sm"
                                outlined
                                ?customFont=${true}
                                @click="${this.emailCaptureSubmit}">
                                <translated-span>Submit</translated-span>
                            </form-button>
        `;
    }

    renderJllCustomForm() {
        return unsafeHTML(getJLLFormHMTL(this.point?.custom_information?.custom_field_entries?.find((entry) => entry.field_id.$oid === '66b4d4ada47d7813b35f128e')?.field_value ?? '') ?? '');
    }

    static get styles() {
        return css`
            :host {
                --mdc-typography-button-text-transform: case;
                --mdc-typography-button-letter-spacing: 0px;
                --mdc-typography-subtitle1-font-size: 12px;
                --mdc-icon-button-size: 26px;
            }

            /* width */
            .expanded-point-card__content::-webkit-scrollbar, .expanded-point-card__content--horizontal::-webkit-scrollbar, .expanded-point-card__formt::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .expanded-point-card__content::-webkit-scrollbar-track, .expanded-point-card__content--horizontal::-webkit-scrollbar-track, .expanded-point-card__form::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .expanded-point-card__content::-webkit-scrollbar-thumb, .expanded-point-card__content--horizontal::-webkit-scrollbar-thumb, .expanded-point-card__for::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .expanded-point-card__content {
                padding: 8px 16px;
                flex: 1 1 auto;
                overflow: hidden auto;
                font-size: .75rem;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
            }

            .expanded-point-card {
                border-radius: 4px;
                background: white;
                position: relative;
                z-index: 1;
                box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px;
            }

            .admin-info-icon {
                --mdc-icon-size: 16px;
                color: var(--main-featured-color);
            }

            .expanded-point-card--discovery {
                background: transparent;
                border: none;
                border-bottom: 0.5px solid var(--theme-input-border-color);
                word-wrap: break-word;
                margin-left: 8px;
            }

            .no-bottom-border {
                border-bottom: none;
            }

            .expanded-point-card__discovery-content {
                float: left;
                width: 100%;
            }

            .expanded-point-card__content--discovery {
                display: flex;
                margin: 4x 8px;
                justify-content: space-between;
                flex-direction: column;
            }

            .expanded-point-card__content--list-of-points {
                display: flex;
                margin: 0;
                justify-content: space-between;
                flex-direction: column;
            }

            .emailInputForm {
                flex: 1;
            }

            .expanded-point-card__upvote-icon {
                width: 16px;
                height: 16px;
            }

            .expanded-point-card__single-image {
                height: 100px;
                width: 150px;
                border-radius: 4px;
                object-fit: cover;
                float: right;
                margin: 10px;
            }

            .expanded-point-card--horizontal {
                width: 100%;
                display: flex;
                flex-direction: column;
                max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 50px);
            }

            .expanded-point-card__form {
                display: flex;
                flex-direction: column;
                justify-content: flex-start;
                margin-top: 10px;
                gap: 10px;
                padding: 1rem;
                flex: 1;
                overflow-y: auto;
                border: solid 1px var(--theme-light-scrollbar-color);
            }

            .expanded-point-card--backpack {
                width: 100%;
                display: flex;
                flex-direction: column;
                height: 300px;
            }

            .expanded-point-card__content--horizontal .expanded-expanded-point-card__title-text {
                font-size: 18px;
                align-self: center;
            }

            .expanded-point-card__point-number {
                margin-right: -5px;
                z-index: 2;
            }

            .expanded-point-card__custom-icon {
                margin-right: -5px;
                z-index: 1;
                border-radius: 100%;
            }

            .expanded-point-card__checked-in {
                margin-right: -5px;
                z-index: 3;
            }

            .expanded-point-card__special-offer {
                border: 1px dashed var(--theme-input-color);
                padding: 4px;
                margin-top: 8px;
                display: inline-block;
            }

            .expanded-point-card__menu-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                top: 4px;
                right: 0;
                z-index: 2;
            }

            .expanded-point-card__edit,
            .expanded-point-card__info,
            .expanded-point-card__close_admin {
                background-color: var(--main-featured-color);
                margin-right: 4px;
                border-radius: 4px;
                border:none;
                --mdc-theme-on-primary: var(--theme-input-color);
            }

            .expanded-point-card__upvote,
            .expanded-point-card__share,
            .expanded-point-card__close_cc {
                background-color: white;
                box-shadow: rgb(31 27 45 / 12%) 0px 2px 8px -4px, rgb(31 27 45 / 12%) 0px 4px 16px;
                --mdc-icon-size: 18px;
                border-radius: 40px;
                margin-right: 6px;
                --mdc-icon-button-size: 30px;
            }

            .expanded-point-card__upvote--user-upvoted {
                background-color: var(--theme-input-color);
                color: white;
            }

            .expanded-point-card__category-wrapper {
                display: flex;
                align-items: center;
                position: absolute;
                bottom: 0;
                background: white;
                padding: 0 10px;
            }

            .expanded-point-card__category-name {
                margin-left: 6px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                max-width: 220px;
                display: inline-block;
            }

            .expanded-point-card__booking {
                display: flex;
                flex-direction: row;
                flex-wrap: none;
                overflow-x: scroll;
                justify-content: flex-start;
                gap: 5px;
                color: var(--theme-input-color);
            }

            .expanded-point-card__booking::-webkit-scrollbar {
                display: none;
            }

            .expanded-point-card__associated-map {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
                cursor: pointer;
                white-space: nowrap;
                overflow: hidden;
                max-width: 300px;
                display: inline-block;
                text-overflow: ellipsis;
            }

            .expanded-point-card__associated-maps {
                display: flex;
                flex-direction: row;
                overflow-x: auto;
                gap: 5px;
                margin-top: 5px;
            }

            .expanded-point-card__associated-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__image-wrapper {
                position: relative;
            }

            .expanded-point-card__image-wrapper--custom {
                position: relative;
                max-height: var(--point-card-image-height, 50%);
                overflow: hidden;
            }

            .expanded-point-card__image-wrapper--no-image {
                position: static;
                display: flex;
                justify-content: space-between;
                padding: 4px 0px;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__category-wrapper {
                position: static;
            }

            .expanded-point-card__image-wrapper--no-image .expanded-point-card__menu-wrapper {
                position: static;
            }

            .expanded-expanded-point-card__title-text {
                font-weight: 700;
                font-size: 1rem;
                margin-bottom: 5px;
                color: var(--main-primary-color);
            }

            .expanded-point-card__source {
                color: var(--theme-input-color);
                border-radius: 9px;
                background: var(--theme-light-scrollbar-color);
                padding: 2px 6px;
                border: 1px solid var(--theme-input-border-color);
                font-weight: bold;
                font-size: 12px;
                letter-spacing: 0.5px;
                text-decoration: none;
            }

            .expanded-point-card__source-wrapper {
                padding-top: 10px;
            }

            .expanded-point-card__address {
                color: var(--theme-input-color);
            }

            .expanded-point-card__address-icon,
            .expanded-point-card__event-icon {
                vertical-align: middle;
                --mdc-icon-size: 14px;
                width: 14px;
            }

            .expanded-point-card__event {
                color: var(--theme-input-color);
            }

            .expanded-point-card__event-label {
                font-weight: 600;
                opacity: 0.8;
            }

            .expanded-point-card__description {
                color: var(--theme-input-color);
            }

            .expanded-expanded-point-card__title--featured {
                color: var(--main-featured-color);
                align-self: center;
                padding-right: 4px;
            }

            .expanded-point-card__title {
                display: flex;
            }

            .point-card__tags-wrapper {
                display: flex;
                flex-wrap: wrap;
                padding: 8px 0;
            }

            .expanded-point-card__tag {
                user-select: none;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 4px 8px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid lightgray;
                background: #fff;
                filter: drop-shadow(rgba(0, 0, 0, 0.25) 0px 1px 1px);
            }

            .expanded-point-card__tag-icon {
                --mdc-icon-size: 16px;
                padding-right: 4px;
            }

            .small-text {
                font-size: 0.75rem;
                line-height: 0.75rem;
            }

            .admin-action-list-holder {
                margin-bottom: 5px;
                margin-top: 0;
            }

            .admin-actions-list {
                padding: 5px;
                background-color: var(--main-featured-color);
                margin: 5px;
                border-radius: 5px;
                cursor: pointer;
                white-space: nowrap;

            }
            .hidden {
                display: none;
            }

            @media only screen and (max-width: 550px) {
                .expanded-point-card--horizontal_admin {
                    max-height: calc(
                        90dvh - ${unsafeCSS(heights.nav)} -
                            ${unsafeCSS(heights.mobileSettingsControls)} - 125px
                    );
                    height: 60vh;
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--horizontal_cc {
                    max-height: calc(90dvh - ${unsafeCSS(heights.nav)} - 75px);
                    display: flex;
                    flex-direction: column;
                }

                .expanded-point-card--discovery {
                    max-height: none;
                    display: block;
                }

                .expanded-point-card__single-image {
                    float: none;
                    width: 100%;
                    height: 150px;
                    margin: 0px;
                }

                .expanded-point-card__content--horizontal {
                    margin-top: 5px;
                    margin-bottom: 5px;
                    overflow: hidden auto;
                    flex: 1 1 auto;
                }
            }
        `;
    }
}
